import { useState } from 'react'
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ReportPager, ReportSection } from '@/components/reports/ReportSection'
import { ReportStates, downloadCsv, tooltipStyle } from '@/components/reports/procedures/reportShared'
import { useInventoryStockReport } from '@/hooks/useProcedureVolumeReport'
import { reportFileName, toCsv } from '@/lib/reportCsv'
import type { ReportFilterSelection } from '@/lib/reportFilters'
import type { ReportTimeFrame } from '@/lib/reportRange'

/**
 * Report 2.1 — items at zero or below minimum level.
 *
 * ## Why this one says "now" and means it
 *
 * Lot quantities are mutated in place, so there is no history to read a past
 * level from. The report takes no window at all rather than accepting one and
 * ignoring it, and says so under the headline — a period control that silently
 * does nothing is how a reader stops trusting the rest of the page.
 *
 * ## Out and below are two different problems
 *
 * Out of stock stops work today; below minimum is a reorder. They are counted
 * apart and coloured apart, never summed into one "items needing attention"
 * number that would hide which is which.
 *
 * Materials holding stock with no minimum set are stated beside the counts.
 * That is missing master data, not a healthy level, and folding it into "ok"
 * would report a catalogue as under control because nobody set thresholds.
 */
const OUT = '#d64545'
const BELOW = '#eda100'
const OK = '#d5dae0'

const PAGE_SIZE = 20

export function InventoryStockReport({
  filters,
}: {
  frame: ReportTimeFrame
  filters: ReportFilterSelection
}) {
  const query = useInventoryStockReport(filters)
  const [page, setPage] = useState(1)
  const state = ReportStates(query)
  if (state) return state
  const report = query.data
  if (!report) return null

  const categories = report.byCategory.map((c) => ({
    name: c.category,
    out: c.out,
    below: c.below,
    ok: c.ok,
  }))

  const pageCount = Math.max(1, Math.ceil(report.rows.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const start = (current - 1) * PAGE_SIZE
  const rows = report.rows.slice(start, start + PAGE_SIZE)

  const exportCsv = () => {
    downloadCsv(
      toCsv(
        ['Material', 'Code', 'Category', 'Type', 'On hand', 'Minimum', 'Short by', 'Reorder point', 'Status'],
        report.rows.map((r) => [
          r.materialName,
          r.materialId,
          r.category,
          r.materialType,
          r.onHand,
          r.minStock,
          r.shortBy,
          r.reorderPoint,
          r.status === 'out' ? 'Out of stock' : 'Below minimum',
        ]),
      ),
      reportFileName('inventory-below-minimum', { from: report.asOf, to: report.asOf }),
    )
  }

  return (
    <div className='flex flex-col gap-4 p-4'>
      <ReportSection title='Stock against minimum' subtitle={`As at ${report.asOf}`}>
        <div className='flex flex-col gap-3 p-4'>
          {/* Stock has no history to read a past level from, so unlike every
              other report here the period genuinely does not apply. */}
          <p className='text-xs text-muted-foreground'>Current levels: the period does not apply.</p>
          <div className='flex flex-wrap items-end gap-x-8 gap-y-3'>
            <div>
              <p className='text-5xl font-semibold tabular-nums' style={{ color: report.out > 0 ? OUT : undefined }}>
                {report.out}
              </p>
              <p className='mt-1 text-sm text-muted-foreground'>out of stock</p>
            </div>
            <dl className='flex flex-wrap gap-x-6 gap-y-2'>
              <Stat label='Below minimum' value={report.below} color={BELOW} />
              <Stat label='At or above' value={report.ok} />
              <Stat label='No minimum set' value={report.noMinimum} />
            </dl>
          </div>
        </div>
      </ReportSection>

      {categories.length > 0 && (
        <ReportSection title='By category'>
          <div className='p-4'>
            <ResponsiveContainer width='100%' height={Math.max(160, categories.length * 34)}>
              <BarChart data={categories} layout='vertical' margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                <XAxis type='number' allowDecimals={false} hide />
                <YAxis
                  type='category'
                  dataKey='name'
                  width={130}
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip contentStyle={tooltipStyle} />
                {/* Stacked, so a category's problems are read against its own
                    size rather than against the worst category's. */}
                <Bar dataKey='out' stackId='s' fill={OUT} name='Out of stock' barSize={18} />
                <Bar dataKey='below' stackId='s' fill={BELOW} name='Below minimum' barSize={18} />
                <Bar dataKey='ok' stackId='s' fill={OK} name='At or above' barSize={18} radius={[0, 4, 4, 0]}>
                  <LabelList dataKey='ok' position='right' style={{ fontSize: 10, fill: '#6b7280' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ReportSection>
      )}

      <ReportSection title='To order' onExport={report.rows.length > 0 ? exportCsv : undefined}>
        {report.rows.length === 0 ? (
          <p className='px-4 py-6 text-xs text-muted-foreground'>
            Everything is at or above its minimum.
          </p>
        ) : (
          <>
            <div className='overflow-x-persistent'>
              <table className='w-full text-xs'>
                <thead className='border-b bg-muted/40 text-left text-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2 font-medium'>Material</th>
                    <th className='px-3 py-2 font-medium'>Category</th>
                    <th className='px-3 py-2 text-right font-medium'>On hand</th>
                    <th className='px-3 py-2 text-right font-medium'>Minimum</th>
                    <th className='px-3 py-2 text-right font-medium'>Short by</th>
                    <th className='px-3 py-2 font-medium'>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className='border-b last:border-0'>
                      <td className='px-3 py-1.5'>
                        {r.materialName}
                        <span className='ml-1.5 text-muted-foreground'>{r.materialId}</span>
                      </td>
                      <td className='px-3 py-1.5'>{r.category}</td>
                      <td className='px-3 py-1.5 text-right tabular-nums'>{r.onHand}</td>
                      <td className='px-3 py-1.5 text-right tabular-nums'>{r.minStock ?? '—'}</td>
                      <td className='px-3 py-1.5 text-right tabular-nums'>{r.shortBy ?? '—'}</td>
                      <td className='px-3 py-1.5 font-medium' style={{ color: r.status === 'out' ? OUT : BELOW }}>
                        {r.status === 'out' ? 'Out of stock' : 'Below minimum'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ReportPager
              page={current}
              pageCount={pageCount}
              from={start + 1}
              to={start + rows.length}
              total={report.rows.length}
              onPage={setPage}
            />
          </>
        )}
      </ReportSection>
    </div>
  )
}

function Stat({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div>
      <dt className='flex items-center gap-1.5 text-xs text-muted-foreground'>
        {color && <span aria-hidden className='h-2 w-2 rounded-sm' style={{ background: color }} />}
        {label}
      </dt>
      <dd className='text-xl font-semibold tabular-nums text-foreground'>{value}</dd>
    </div>
  )
}
